import { observer } from 'mobx-react-lite'
import { Tooltip, Typography } from '@mui/material'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import { API_BASE_URL } from 'src/util/constant'
import { AppAvatar } from 'src/component/common/analog/molecule/AppAvatar'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'

type ProjectUser = {
  userId: string
  avatarUrl?: string
}

export type ProjectListCardProps = {
  projectId: string
  projectName: string
  users: ProjectUser[]
  startDate?: string
  endDate?: string
  onShowProject: (projectId: string, projectName: string) => void
  onDeleteProject: (projectId: string) => void
  isSelected: boolean
}

export const ProjectListCard = observer(({ projectId, projectName, users, startDate, endDate, onShowProject, onDeleteProject, isSelected }: ProjectListCardProps) => {
  const { locale, translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder
  const visibleUsers = users.slice(0, 2)
  const remainingUsers = users.length - visibleUsers.length

  const formattedDate = startDate
    ? new Date(startDate).toLocaleDateString(
      locale === 'sv-SE' ? 'sv-SE' : 'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      },
    )
    : ''

  const now = new Date()
  const start = startDate ? new Date(startDate) : null
  const end = endDate ? new Date(endDate) : null

  const projectStatus = start && end
    ? now < start
      ? projectBuilderTranslation.planned()
      : now > end
        ? projectBuilderTranslation.finished()
        : projectBuilderTranslation.active()
    : ''

  return (
    <AppPaper
      sx={[
        styles.root,
        (isSelected && { backgroundColor: ColorTokens.primary10 }),
      ]}
      onClick={() => onShowProject(projectId, projectName)}
    >
      <AppBox className='leftBar'
        sx={styles.leftBar(isSelected)}
      />
      <AppBox sx={styles.content}>
        <AppBox sx={styles.header}>
          <Typography sx={styles.statusBadge}>
            {projectStatus}
          </Typography>

          <AppBox
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: ColorTokens.red10,
              borderRadius: '999px',
              width: 30,
              height: 30,
            }}>
            <DeleteOutlineOutlinedIcon
              fontSize='small'
              sx={styles.deleteIcon}
              onClick={(e) => {
                e.stopPropagation()
                onDeleteProject(projectId)
              }}
            />
          </AppBox>
        </AppBox>
        <Tooltip title={projectName} placement='top' arrow>
          <Typography variant='h4' noWrap>
            {projectName}
          </Typography>
        </Tooltip>

        <AppBox sx={styles.divider} />

        <AppBox sx={styles.footer}>
          <AppBox sx={styles.users}>
            {visibleUsers.map(user => (
              <AppBox
                key={user.userId}
                sx={styles.avatarWrapper}
              >
                <AppAvatar
                  src={`${API_BASE_URL}${user.avatarUrl}`}
                  outerProps={{ squareSize: 40, sx: styles.avatar }}
                />
              </AppBox>
            ))}

            {remainingUsers > 0 && (
              <AppBox sx={styles.remainingUsers}>
                <Typography sx={styles.remainingUsersText}>
                  +{remainingUsers}
                </Typography>
              </AppBox>
            )}
          </AppBox>
          <Typography sx={styles.date}>
            {formattedDate}
          </Typography>
        </AppBox>
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  root: {
    position: 'relative',
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    padding: '10px 15px',
    transition: 'border-color 0.2s ease-in-out',
    border: '1px solid',
    borderColor: 'transparent',
    backgroundColor: 'background.dark',
    '&:hover': {
      cursor: 'pointer',
      borderColor: ColorTokens.primary10,
    },
  },
  leftBar: (isSelected: boolean) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '5px',
    height: '100%',
    backgroundColor: isSelected
      ? ColorTokens.primary200
      : ColorTokens.secondary500,
    transition: 'background-color 0.2s ease',
  }),
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3px 7px',
    borderRadius: '20px',
    backgroundColor: ColorTokens.primary200,
    fontSize: 12,
  },
  deleteIcon: {
    color: ColorTokens.red200,
    cursor: 'pointer',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: ColorTokens.neutral200,
    marginTop: 'auto',
  },
  footer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  users: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto',
    paddingLeft: 4,
  },
  avatarWrapper: {
    marginLeft: '-15px',
    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  avatar: {
    border: `1px solid ${ColorTokens.neutral100}`,
    borderRadius: '50%',
  },
  remainingUsers: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: ColorTokens.neutral300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: '-15px',
    border: `1px solid ${ColorTokens.neutral100}`,
  },
  remainingUsersText: {
    fontSize: 12,
    fontWeight: 700,
  },
  date: {
    fontSize: 12,
    color: 'text.secondary',
  },
}